import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PublicLayout } from '../components/Layout';
import { Skeleton } from '../components/primitives';
import { ProviderEmbed } from '../embeds/registry';
import { useSnapshot } from '../useSnapshot';
import { NotFound } from './NotFound';

export function FormPage() {
  const { section, name } = useParams();
  const slug = section && name ? `${section}/${name}` : null;
  const { snapshot, error, loading, retry } = useSnapshot();

  const form = snapshot
    ? Object.values(snapshot.sections)
        .flatMap((s) => s.forms)
        .find((f) => f.slug === slug)
    : undefined;

  // The visible H1 is gone (PandaDoc renders its own title) — keep the tab title useful.
  useEffect(() => {
    if (form) document.title = `${form.displayName} — Knocking Forms`;
    return () => {
      document.title = 'Knocking Forms';
    };
  }, [form]);

  if (!loading && !error && snapshot) {
    if (!form) return <NotFound reason="form" />;

    const sectionKey = form.slug.split('/')[0]!;
    const sectionLabel = snapshot.sections[sectionKey]?.label ?? sectionKey;

    return (
      <PublicLayout backTo={{ label: sectionLabel, to: `/${sectionKey}` }}>
        <div className="mx-auto w-full max-w-3xl px-2 pt-4 sm:px-4 sm:pt-6">
          <ProviderEmbed form={form} />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto w-full max-w-3xl px-2 pt-4 sm:px-4 sm:pt-6">
        {error ? (
          <div role="alert" className="mt-8 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm">
            <p>We couldn't load this form right now ({error}).</p>
            <button type="button" onClick={retry} className="mt-2 font-medium underline underline-offset-2">
              Try again
            </button>
          </div>
        ) : (
          <div aria-label="Loading form">
            <Skeleton className="h-[calc(100dvh-160px)] w-full" />
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
